import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { roomCodeFragment } from "../../../../packages/session-code/src";
import { Button } from "../components/Button";
import { ScreenShell } from "../components/ScreenShell";
import { useUiText } from "../i18n/useUiText";
import { useAppStore } from "../state/useAppStore";

export function RoomScreen() {
  const t = useUiText();
  const session = useAppStore((state) => state.session);
  const goHome = useAppStore((state) => state.goHome);
  const proceedToSeat = useAppStore((state) => state.proceedToSeat);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle"
  );

  const roomCode = session?.sessionCode ?? "";
  const shareUrl = useMemo(() => {
    if (roomCode === "") {
      return "";
    }
    const base = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    return `${base}${roomCodeFragment(roomCode)}`;
  }, [roomCode]);

  useEffect(() => {
    if (shareUrl === "") {
      return;
    }
    let active = true;
    void QRCode.toDataURL(shareUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 260,
      color: { dark: "#2c251e", light: "#fffaf0" }
    }).then(
      (dataUrl) => {
        if (active) setQrDataUrl(dataUrl);
      },
      () => {
        if (active) setQrDataUrl(null);
      }
    );
    return () => {
      active = false;
    };
  }, [shareUrl]);

  if (session === null) {
    return null;
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  return (
    <ScreenShell
      backLabel={t("common.home")}
      footer={
        <Button onClick={proceedToSeat} wide>
          {t("room.continue")}
        </Button>
      }
      onBack={goHome}
    >
      <div className="narrow-column room-screen">
        <header className="screen-heading screen-heading--centered">
          <h1>{t("room.title")}</h1>
          <p>{t("room.body")}</p>
        </header>

        <div className="room-qr">
          {qrDataUrl === null ? (
            <div className="qr-placeholder" aria-label={t("room.qrLoading")} />
          ) : (
            <img alt={t("room.qrAlt")} height="260" src={qrDataUrl} width="260" />
          )}
        </div>

        <div className="room-code">
          <span>{t("room.codeLabel")}</span>
          <strong aria-label={roomCode.split("").join(" ")}>{roomCode}</strong>
        </div>

        <Button onClick={() => void copy()} variant="secondary" wide>
          {copyState === "copied" ? t("room.copied") : t("room.copy")}
        </Button>
        {copyState === "failed" ? (
          <p className="inline-error" role="alert">
            {t("room.copyFailed")}
          </p>
        ) : null}
      </div>
    </ScreenShell>
  );
}
